/**
 * @fileoverview Loads the student's own tracker for one project and keeps it
 * fresh: silent reloads after a drawer writes, and a poll while the backend is
 * still processing the row.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchStudentTrackMatrix, StudentTrackerNotEnrolledError } from '../service/studentTrackerService'
import type { StudentTrackerMatrix } from '../types'
import { useProcessingPoll } from './useProcessingPoll'

export type StudentTrackerLoadState = 'idle' | 'loaded' | 'not_enrolled' | 'error'

interface UseStudentTrackerArgs {
  projectId: string
  /** Only resolves a toast; the latest reference is captured by closure. */
  onLoadError: () => void
}

interface UseStudentTrackerReturn {
  matrix: StudentTrackerMatrix | null
  loading: boolean
  state: StudentTrackerLoadState
  /** Reload with the skeleton (after a failure). */
  reload: () => void
  /** Reload keeping the table on screen (after a drawer submits / the processing poll). */
  reloadSilent: () => void
}

export function useStudentTracker({ projectId, onLoadError }: UseStudentTrackerArgs): UseStudentTrackerReturn {
  const [matrix, setMatrix] = useState<StudentTrackerMatrix | null>(null)
  const [loading, setLoading] = useState(Boolean(projectId))
  const [state, setState] = useState<StudentTrackerLoadState>('idle')

  const mountedRef = useRef(true)
  // Requests are not abortable in the API helper; a token discards stale responses after a project change.
  const requestRef = useRef(0)
  const onLoadErrorRef = useRef(onLoadError)
  onLoadErrorRef.current = onLoadError

  const load = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!projectId) {
        setMatrix(null)
        setState('idle')
        setLoading(false)
        return
      }
      const token = ++requestRef.current
      if (!silent) setLoading(true)
      try {
        const data = await fetchStudentTrackMatrix(projectId)
        if (!mountedRef.current || token !== requestRef.current) return
        setMatrix(data)
        setState('loaded')
      } catch (error) {
        if (!mountedRef.current || token !== requestRef.current) return
        if (silent) return // keep the current data; the next poll or a manual reload retries
        setMatrix(null)
        if (error instanceof StudentTrackerNotEnrolledError) {
          setState('not_enrolled')
        } else {
          setState('error')
          onLoadErrorRef.current()
        }
      } finally {
        if (mountedRef.current && token === requestRef.current && !silent) setLoading(false)
      }
    },
    [projectId],
  )

  useEffect(() => {
    void load()
  }, [load])

  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )

  const reload = useCallback(() => void load(), [load])
  const reloadSilent = useCallback(() => void load({ silent: true }), [load])

  useProcessingPoll(Boolean(matrix?.processing), reloadSilent)

  return { matrix, loading, state, reload, reloadSilent }
}
